// ============================================================================
// Custom Best Stories Component
// ============================================================================
// Featured articles section for the research landing page.
// Shows 1 large featured article + 2 smaller secondary articles side-by-side.
//
// Layout:
// - Desktop: Featured (left) + 2 Secondary (right column, stacked)
// - Mobile: All stacked vertically
//
// Articles: Top 3 newest (sorted by published_at in parent component)
//
// Note: Uses .research-featured-article class which has the 10px margin-bottom
// we added during review for better mobile spacing.
// ============================================================================

import React from 'react';

interface Props {
    stories: any[];  // Array of article objects (newest 3)
    onArticleClick: (slug: string) => void;  // Navigate to article
}

export const CustomBestStories: React.FC<Props> = ({ stories, onArticleClick }) => {
    // Defensive programming - ensure we have valid stories
    if (!stories || stories.length === 0) {
        return null;
    }

    const featuredStory = stories[0];
    const sideStories = stories.slice(1, 3);

    // Additional safety check
    if (!featuredStory) {
        return null;
    }

    return (
        <section className="research-best-stories">
            <div className="best-stories-container">
                <div className="research-divider-split"></div>

                <div className="best-stories-grid">
                    {/* Large Featured Article */}
                    <article className="research-featured-article" onClick={() => onArticleClick(featuredStory.slug)}>
                        <div className="featured-image">
                            <img
                                src={featuredStory.imageUrl}
                                alt={featuredStory.imageAlt}
                            />
                            {/* Featured story should ALWAYS have tag on image - all screen sizes */}
                            <span className="research-tag-overlay" style={{
                                position: 'absolute',
                                bottom: '12px',
                                left: '12px'
                            }}>
                                {featuredStory.tag}
                            </span>
                        </div>
                        <h3 className="featured-title">
                            {featuredStory.title}
                        </h3>
                    </article>

                    {/* Side Stories Column */}
                    <div className="research-secondary-stories">
                        {sideStories.map((story) => (
                            <article
                                key={story.slug}
                                className="research-secondary-article"
                                onClick={() => onArticleClick(story.slug)}
                            >
                                <div className="secondary-image">
                                    <img
                                        src={story.imageUrl}
                                        alt={story.imageAlt}
                                    />
                                    <span className="research-tag-overlay research-tag-overlay-image">
                                        {story.tag}
                                    </span>
                                </div>
                                <div className="secondary-content">
                                    <span className="research-tag-overlay research-tag-overlay-content">
                                        {story.tag}
                                    </span>
                                    <h4 className="secondary-title">
                                        {story.title}
                                    </h4>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
